import { useState, useEffect } from "react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { Plus, Filter, Loader2, Store, Heart, Sparkles, Search, UserPlus, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { FollowButton } from "@/components/FollowButton";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card } from "@/components/ui/card";

import { PostCard } from "@/components/PostCard";
import { RestyleProjectCard } from "@/components/RestyleProjectCard";
import { PostComposerModal } from "@/components/PostComposerModal";
import { PostDetailModal } from "@/components/PostDetailModal";
import { LoginModal } from "@/components/LoginModal";
import { useAuth } from "@/lib/authContext";
import { POST_CATEGORIES, PROVINCES } from "@shared/schema";
import type { Post } from "@shared/schema";
import { useI18n } from "@/lib/i18n";
import { useShopOwnerFeatures } from "@/lib/featureFlags";
import { getPreferredGeo } from "@/lib/geoPreferences";

export default function CommunityPage() {
  const { isLoggedIn, user } = useAuth();
  const { locale, t } = useI18n();
  const { canSeeShopOwner } = useShopOwnerFeatures();
  useDocumentTitle("nav.community");
  const preferredGeo = getPreferredGeo(user, locale);
  const preferredCountry = preferredGeo.country;
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showComposer, setShowComposer] = useState(false);

  // Filters
  const [category, setCategory] = useState<string>("all");
  const [region, setRegion] = useState<string>("all");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  // New Features State
  const [restyleProjects, setRestyleProjects] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Pagination / Limits
  const [limitTreasures, setLimitTreasures] = useState(4);
  const [limitUpdates, setLimitUpdates] = useState(4);
  const [limitRestyles, setLimitRestyles] = useState(4);

  // Deep-linking state
  const [deepLinkPost, setDeepLinkPost] = useState<Post | null>(null);
  const [showDeepLinkDetail, setShowDeepLinkDetail] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Check for deep-linked post on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const postId = params.get("post");

    if (postId) {
      const fetchDeepLinkPost = async () => {
        try {
          const res = await fetch(`/api/posts/${postId}`);
          if (res.ok) {
            const data = await res.json();
            setDeepLinkPost(data.post);
            setShowDeepLinkDetail(true);
          }
        } catch (err) {
          console.error("Failed to fetch deep-linked post", err);
        }
      };
      fetchDeepLinkPost();
    }
  }, []);

  // Fetch posts from API
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await fetch("/api/posts");
        if (response.ok) {
          const data = await response.json();
          setPosts(data);
        } else {
          console.error("Failed to fetch posts");
        }
      } catch (error) {
        console.error("Error fetching posts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Fetch Restyle Projects
  useEffect(() => {
    const fetchRestyles = async () => {
      try {
        const query = new URLSearchParams();
        if (debouncedSearch) query.append("search", debouncedSearch);
        query.append("limit", "50");

        const res = await fetch(`/api/restyle/gallery?${query.toString()}`);
        if (res.ok) {
          const data = await res.json();
          setRestyleProjects(data);
        }
      } catch (err) {
        console.error("Failed to fetch restyles", err);
      }
    };
    fetchRestyles();
  }, [debouncedSearch]);

  const handleShare = () => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
    } else {
      setShowComposer(true);
    }
  };

  const handlePostCreated = async () => {
    try {
      const response = await fetch("/api/posts");
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error("Error refreshing posts:", error);
    }
  };

  // Community Search for Users
  const { data: searchResults, isLoading: isSearching } = useQuery<any[]>({
    queryKey: [`/api/profiles/search?q=${debouncedSearch}`],
    enabled: debouncedSearch.length > 1,
  });

  // Social Feed Data
  const { data: socialFeed, isLoading: isLoadingFeed } = useQuery<any[]>({
    queryKey: ["/api/social/feed"],
    enabled: isLoggedIn,
  });

  // Base Filtering (Category & Region)
  const safePosts = Array.isArray(posts) ? posts : [];
  const baseFilteredPosts = safePosts.filter(post => {
    if (!post) return false;
    if (category && category !== "all" && (post as any).category !== category) {
      return false;
    }
    return true;
  });

  // Search Filter for Posts
  const searchedPosts = baseFilteredPosts.filter(post => {
    if (!debouncedSearch) return true;
    const term = debouncedSearch.toLowerCase();
    return (
      (post.caption?.toLowerCase().includes(term)) ||
      ((post as any).shopName?.toLowerCase().includes(term)) ||
      ((post as any).shopCity?.toLowerCase().includes(term)) ||
      ((post as any).authorUsername?.toLowerCase().includes(term))
    );
  });

  const byCountryAndPopularity = (a: any, b: any) => {
    const aLocal = (a?.shopCountry || "").toUpperCase() === preferredCountry.toUpperCase();
    const bLocal = (b?.shopCountry || "").toUpperCase() === preferredCountry.toUpperCase();
    if (aLocal !== bLocal) return aLocal ? -1 : 1;
    const aScore = Number(a?.likesCount || 0) + Number(a?.commentsCount || 0) * 2;
    const bScore = Number(b?.likesCount || 0) + Number(b?.commentsCount || 0) * 2;
    return bScore - aScore;
  };

  const userTreasures = searchedPosts
    .filter(p => !p.asShopId)
    .sort(byCountryAndPopularity);

  const visibleTreasures = userTreasures.slice(0, limitTreasures);

  let shopUpdates = searchedPosts.filter(p => !!p.asShopId).sort(byCountryAndPopularity);

  if (showFavoritesOnly && isLoggedIn && user) {
    const allLikedIds = [...(user.likedShopIds || []), ...(user.savedShopIds || [])];
    if (allLikedIds.length > 0) {
      shopUpdates = shopUpdates.filter(p => p.asShopId && allLikedIds.includes(p.asShopId));
    } else {
      shopUpdates = [];
    }
  }

  const visibleUpdates = shopUpdates.slice(0, limitUpdates);
  const visibleRestyles = restyleProjects.slice(0, limitRestyles);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <main className="flex-1 container mx-auto px-4 py-8 space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">{t("community.title")}</h1>
            <p className="text-muted-foreground">{t("community.subtitle")}</p>
          </div>
          <Button onClick={handleShare}>
            <Plus className="h-4 w-4 mr-2" />
            {t("community.shareFind")}
          </Button>
        </div>

        <Tabs defaultValue="explore" className="space-y-8">
          <TabsList className="bg-transparent border-b rounded-none w-full justify-start h-auto p-0 space-x-6">
            <TabsTrigger
              value="explore"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-2 py-3 h-auto"
            >
              {t("community.tabs.explore")}
            </TabsTrigger>
            <TabsTrigger
              value="schatzoekers"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-2 py-3 h-auto"
            >
              {t("community.tabs.treasureHunters")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="explore" className="space-y-12 mt-0">
            <div className="space-y-4 pt-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("community.searchPlaceholder")}
                  className="pl-9 h-12 text-base"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {isLoggedIn && (
                <div className="flex flex-col sm:flex-row gap-3">
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="w-full sm:w-[180px]">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder={t("community.category")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("community.allCategories")}</SelectItem>
                      {POST_CATEGORIES.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={region} onValueChange={setRegion}>
                    <SelectTrigger className="w-full sm:w-[180px]">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder={t("community.region")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("community.allRegions")}</SelectItem>
                      {PROVINCES.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {loading ? (
              <div className="text-center py-16">
                <Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground mb-4" />
                <p className="text-muted-foreground">{t("community.loadingPosts")}</p>
              </div>
            ) : (
              <div className="space-y-12">
                <section>
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Sparkles className="h-5 w-5 text-primary" />
                    </div>
                    <h2 className="text-2xl font-bold">{t("community.userGems")}</h2>
                  </div>
                  {visibleTreasures.length > 0 ? (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {visibleTreasures.map(post => (
                          <PostCard key={post.id} post={post} onLikeChange={handlePostCreated} />
                        ))}
                      </div>
                      {userTreasures.length > limitTreasures && (
                        <div className="flex justify-center mt-6">
                          <Button variant="outline" onClick={() => setLimitTreasures(prev => prev + 8)}>
                            {t("community.viewMore")}
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-muted-foreground italic">{t("community.noGems")}</p>
                  )}
                </section>

                {canSeeShopOwner && (
                  <section>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Store className="h-5 w-5 text-primary" />
                        </div>
                        <h2 className="text-2xl font-bold">{t("community.shopUpdates")}</h2>
                      </div>
                      {isLoggedIn && (
                        <div className="flex items-center space-x-2">
                          <Switch id="favorites-mode" checked={showFavoritesOnly} onCheckedChange={setShowFavoritesOnly} />
                          <Label htmlFor="favorites-mode" className="cursor-pointer">{t("community.myFavoriteShops")}</Label>
                        </div>
                      )}
                    </div>
                    {visibleUpdates.length > 0 ? (
                      <>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                          {visibleUpdates.map(post => (
                            <PostCard key={post.id} post={post} onLikeChange={handlePostCreated} />
                          ))}
                        </div>
                        {shopUpdates.length > limitUpdates && (
                          <div className="flex justify-center mt-6">
                            <Button variant="outline" onClick={() => setLimitUpdates(prev => prev + 8)}>
                              {t("community.viewMore")}
                            </Button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-12 bg-muted/20 rounded-xl">
                        {showFavoritesOnly ? (
                          <>
                            <Heart className="h-10 w-10 mx-auto text-muted-foreground mb-3 opacity-50" />
                            <h3 className="font-semibold text-lg">{t("community.noFavoriteUpdates")}</h3>
                            <p className="text-sm text-muted-foreground mt-1">{t("community.likeShopsHint")}</p>
                          </>
                        ) : (
                          <p className="text-muted-foreground">{t("community.noShopUpdates")}</p>
                        )}
                      </div>
                    )}
                  </section>
                )}

                <section>
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Sparkles className="h-5 w-5 text-primary" />
                    </div>
                    <h2 className="text-2xl font-bold">{t("community.restyles")}</h2>
                  </div>
                  {visibleRestyles.length > 0 ? (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {visibleRestyles.filter((item: any) => !!item?.id).map((item: any) => (
                          <RestyleProjectCard key={item.id} project={item} />
                        ))}
                      </div>
                      {restyleProjects.length > limitRestyles && (
                        <div className="flex justify-center mt-6">
                          <Button variant="outline" onClick={() => setLimitRestyles(prev => prev + 8)}>
                            {t("community.viewMore")}
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-12 bg-muted/20 rounded-xl">
                      <p className="text-muted-foreground">{t("community.noRestyles")}</p>
                    </div>
                  )}
                </section>
              </div>
            )}
          </TabsContent>

          <TabsContent value="schatzoekers">
            <div className="max-w-4xl mx-auto space-y-8">
              <Tabs defaultValue={isLoggedIn ? "following" : "search"} className="space-y-6">
                <div className="flex justify-center">
                  <TabsList className="bg-muted/50 p-1">
                    {isLoggedIn && (
                      <TabsTrigger value="following" className="px-6">{t("social.following")}</TabsTrigger>
                    )}
                    <TabsTrigger value="search" className="px-6">{t("common.search")}</TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="following" className="mt-0">
                  {isLoadingFeed ? (
                    <div className="flex justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  ) : !socialFeed || socialFeed.length === 0 ? (
                    <div className="text-center py-24 bg-muted/20 rounded-2xl border-2 border-dashed">
                      <UserPlus className="h-12 w-12 mx-auto text-muted-foreground mb-4 opacity-20" />
                      <h3 className="text-xl font-bold mb-2">{t("social.feed.noUpdates")}</h3>
                      <p className="text-muted-foreground mb-8 text-balance">
                        {t("social.feed.empty")}
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-8">
                      {socialFeed.map((item: any) => (
                        <div key={`${item.type}-${item.id}`} className="space-y-4">
                          <div className="flex items-center gap-3 mb-2 px-2">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={item.author.avatar_url} />
                              <AvatarFallback>{(item.author.username || item.author.name)?.substring(0, 2).toUpperCase()}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <Link href={item.type === 'shop_post' ? `/kringloopwinkels/dummy/dummy/${item.author.name}` : `/u/${item.author.username || item.author.id}`}>
                                <span className="font-bold cursor-pointer hover:underline text-sm">{item.author.name || item.author.username}</span>
                              </Link>
                              <span className="text-[10px] text-muted-foreground block uppercase font-bold tracking-tight">
                                {item.type === 'shop_post' ? t("social.feed.shopUpdate") : t("social.feed.postedTreasure")}
                              </span>
                            </div>
                            <span className="text-xs text-muted-foreground">{new Date(item.created_at).toLocaleDateString()}</span>
                          </div>
                          <PostCard post={item} onLikeChange={handlePostCreated} />
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="search" className="space-y-6 mt-0">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={t("social.search.placeholder")}
                      className="pl-9 h-12"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  <div className="space-y-4">
                    {isSearching ? (
                      <div className="flex justify-center p-8">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      </div>
                    ) : !searchResults || searchResults.length === 0 ? (
                      debouncedSearch.length > 1 ? (
                        <p className="text-center text-muted-foreground py-8">{t("social.noUsersFoundFor", { search: debouncedSearch })}</p>
                      ) : (
                        <div className="text-center py-12 text-muted-foreground">
                          <Search className="h-10 w-10 mx-auto mb-4 opacity-20" />
                          <p>{t("social.search.minChars")}</p>
                        </div>
                      )
                    ) : (
                      searchResults.map((p: any) => (
                        <Card key={p.id} className="p-4 hover:border-primary/30 transition-colors">
                          <div className="flex items-center gap-4">
                            <Avatar className="h-12 w-12">
                              <AvatarImage src={p.avatar_url} />
                              <AvatarFallback>{p.username?.substring(0, 2).toUpperCase()}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <Link href={`/u/${p.username || p.id}`}>
                                <h4 className="font-bold hover:underline cursor-pointer truncate">
                                  {p.name || p.username}
                                </h4>
                              </Link>
                              <p className="text-sm text-muted-foreground">@{p.username}</p>
                              {p.location && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                <MapPin className="h-3 w-3" /> {p.location}
                              </p>}
                            </div>
                            <FollowButton targetId={p.id} type="user" />
                          </div>
                        </Card>
                      ))
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} action="post" />
      <PostComposerModal open={showComposer} onOpenChange={(open) => { setShowComposer(open); if (!open) handlePostCreated(); }} />
      <PostDetailModal open={showDeepLinkDetail} onOpenChange={setShowDeepLinkDetail} post={deepLinkPost} onLikeChange={handlePostCreated} />
    </div>
  );
}
